import { useState, useEffect, useCallback } from 'react';
import { useDBA } from '@/contexts/DBAContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { Calculator, DollarSign, Target, TrendingUp, Trophy } from 'lucide-react';
import { HighVisibilityPageHeader } from '@/components/ui/high-visibility-mobile';

const STAGE_COLORS = { new: '#0ea5e9', contacted: '#8b5cf6', quoted: '#f59e0b', won: '#10b981', lost: '#ef4444' };
const STATUS_COLORS = { scheduled: '#0ea5e9', in_progress: '#f59e0b', completed: '#10b981', cancelled: '#6b7280' };
const ACCURACY_COLORS = { excellent: '#10b981', good: '#0ea5e9', needs_review: '#f59e0b', high_variance: '#ef4444' };
const money = (value) => `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const pct = (value) => `${Number(value || 0).toFixed(1)}%`;

export default function ReportsPage() {
  const { selectedDBA } = useDBA();
  const [leadReport, setLeadReport] = useState(null);
  const [jobReport, setJobReport] = useState(null);
  const [staffReport, setStaffReport] = useState(null);
  const [revenueReport, setRevenueReport] = useState(null);
  const [accuracyReport, setAccuracyReport] = useState(null);
  const [profitabilityReport, setProfitabilityReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedDBA ? { dba_id: selectedDBA } : {};
      const [leads, jobs, staff, revenue, accuracy, profitability] = await Promise.all([
        api.get('/reports/leads', { params }),
        api.get('/reports/jobs', { params }),
        api.get('/reports/staff', { params }),
        api.get('/reports/revenue', { params }),
        api.get('/reports/estimate-accuracy', { params: { ...params, days: 180 } }),
        api.get('/reports/profitability', { params: { ...params, days: 180 } }),
      ]);
      setLeadReport(leads.data);
      setJobReport(jobs.data);
      setStaffReport(staff.data);
      setRevenueReport(revenue.data);
      setAccuracyReport(accuracy.data);
      setProfitabilityReport(profitability.data);
    } catch (error) {
      setError(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [selectedDBA]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-40 rounded-[1.75rem] bg-blue-100 animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1,2,3,4].map(i => <div key={i} className="h-72 rounded-[1.5rem] bg-blue-100 animate-pulse" />)}
        </div>
      </div>
    );
  }

  const leadStageData = leadReport?.by_stage?.map(s => ({
    name: s.stage, count: s.count, value: s.value, fill: STAGE_COLORS[s.stage] || '#71717a'
  })) || [];

  const leadSourceData = leadReport?.by_source?.map(s => ({
    name: s.source || 'Unknown', count: s.count
  })) || [];

  const jobStatusData = jobReport?.by_status?.map(s => ({
    name: s.status?.replace('_', ' '), count: s.count, value: s.value, fill: STATUS_COLORS[s.status] || '#71717a'
  })) || [];

  const staffData = staffReport?.map(s => ({
    name: s.name?.split(' ')[0] || 'N/A',
    assigned: s.assigned_jobs,
    completed: s.completed_jobs,
    in_progress: s.in_progress_jobs,
  })) || [];

  const monthlyRevenue = revenueReport?.monthly?.map(m => ({
    name: m.month, revenue: m.revenue, count: m.count
  })) || [];

  const dbaRevenue = revenueReport?.by_dba?.map(d => ({
    name: d.name?.split(' ')[0] || 'N/A', revenue: d.revenue, count: d.count
  })) || [];

  const accuracySummary = accuracyReport?.summary || {};
  const accuracyBucketData = Object.entries(accuracyReport?.bucket_counts || {}).map(([key, count]) => ({
    name: key.replaceAll('_', ' '), count, fill: ACCURACY_COLORS[key] || '#71717a'
  }));
  const accuracyByCategory = (accuracyReport?.by_load_category || []).slice(0, 8).map(item => ({
    name: item.label || 'Unknown', accuracy: item.avg_accuracy_percent, priceVariance: item.avg_price_variance, count: item.count
  }));
  const recentAccuracy = accuracyReport?.recent_records || [];
  const profitSummary = profitabilityReport?.summary || {};
  const profitByJobType = (profitabilityReport?.by_job_type || []).slice(0, 8).map(item => ({ name: item.label, profit: item.profit, revenue: item.revenue, margin: item.avg_margin, count: item.count }));
  const profitByLeadSource = (profitabilityReport?.by_lead_source || []).slice(0, 8).map(item => ({ name: item.label, profit: item.profit, revenue: item.revenue, margin: item.avg_margin, count: item.count }));
  const topLeadSources = profitabilityReport?.top_lead_sources || [];
  const topJobCategories = profitabilityReport?.top_job_categories || [];

  return (
    <div className="-mx-3 space-y-6 bg-[#eef7ff] px-0 pb-8 pt-0 animate-fade-in sm:mx-0 sm:bg-transparent sm:px-0 sm:pt-0">
      <HighVisibilityPageHeader
        eyebrow="Business intelligence"
        title="Reports"
        subtitle="Revenue, profit, lead flow, accuracy, and crew performance in one readable view."
        countLabel={`${money(revenueReport?.total_paid)} revenue · ${money(profitSummary.total_profit)} profit`}
        icon={TrendingUp}
        tone="emerald"
        testId="reports-high-visibility-header"
        titleTestId="reports-title"
      />

      <div className="space-y-6 px-3 sm:px-0">

      {/* Revenue KPIs */}
      {revenueReport && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="revenue-kpis">
          <Card className="rounded-[1.5rem] border border-emerald-100 bg-white shadow-sm sm:rounded-md"><CardContent className="p-4">
            <p className="text-[10px] font-black uppercase tracking-wider text-emerald-700">Total Revenue</p>
            <p className="text-3xl font-black text-emerald-600 font-heading">${(revenueReport.total_paid || 0).toLocaleString()}</p>
          </CardContent></Card>
          <Card className="rounded-[1.5rem] border border-amber-100 bg-white shadow-sm sm:rounded-md"><CardContent className="p-4">
            <p className="text-[10px] font-black uppercase tracking-wider text-amber-700">Outstanding</p>
            <p className="text-3xl font-black text-amber-600 font-heading">${(revenueReport.outstanding || 0).toLocaleString()}</p>
          </CardContent></Card>
          <Card className="rounded-[1.5rem] border border-sky-100 bg-white shadow-sm sm:rounded-md"><CardContent className="p-4">
            <p className="text-[10px] font-black uppercase tracking-wider text-sky-700">Pending Quotes</p>
            <p className="text-3xl font-black text-sky-600 font-heading">${(revenueReport.pending_quotes_value || 0).toLocaleString()}</p>
            <p className="text-xs font-bold text-sky-500">{revenueReport.pending_quotes_count || 0} quotes</p>
          </CardContent></Card>
          <Card className="rounded-[1.5rem] border border-violet-100 bg-white shadow-sm sm:rounded-md"><CardContent className="p-4">
            <p className="text-[10px] font-black uppercase tracking-wider text-violet-700">Pipeline Total</p>
            <p className="text-3xl font-black text-violet-700 font-heading">${((revenueReport.total_paid || 0) + (revenueReport.outstanding || 0)).toLocaleString()}</p>
          </CardContent></Card>
        </div>
      )}

      {/* Revenue Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="rounded-[1.5rem] border border-emerald-100 bg-white shadow-sm sm:rounded-md">
          <CardHeader className="pb-2"><CardTitle className="text-xl font-black font-heading flex items-center gap-2 sm:text-base sm:font-bold"><TrendingUp className="w-5 h-5 text-emerald-600 sm:w-4 sm:h-4" /> Monthly Revenue</CardTitle></CardHeader>
          <CardContent data-testid="monthly-revenue-chart">
            {monthlyRevenue.length === 0 ? (
              <p className="text-sm text-zinc-500 py-8 text-center">No revenue data yet</p>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={monthlyRevenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                  <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-[1.5rem] border border-blue-100 bg-white shadow-sm sm:rounded-md">
          <CardHeader className="pb-2"><CardTitle className="text-xl font-black font-heading flex items-center gap-2 sm:text-base sm:font-bold"><DollarSign className="w-5 h-5 text-blue-600 sm:w-4 sm:h-4" /> Revenue by Business</CardTitle></CardHeader>
          <CardContent data-testid="dba-revenue-chart">
            {dbaRevenue.length === 0 ? (
              <p className="text-sm text-zinc-500 py-8 text-center">No revenue data yet</p>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={dbaRevenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                  <Bar dataKey="revenue" fill="#2563eb" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <section className="space-y-4" data-testid="estimate-accuracy-report-section">
        <div className="flex flex-col gap-1">
          <h2 className="text-2xl font-black tracking-tight text-zinc-950 font-heading flex items-center gap-2" data-testid="estimate-accuracy-title"><Target className="h-6 w-6 text-blue-600" /> Estimate Accuracy</h2>
          <p className="text-base font-semibold text-zinc-600">Compare saved estimates against recorded actual job profit over the last 180 days.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="estimate-accuracy-kpis">
          <MetricCard label="Recorded Actuals" value={accuracySummary.recorded_actuals || 0} tone="text-zinc-950" testId="accuracy-recorded-actuals" />
          <MetricCard label="Avg Accuracy" value={pct(accuracySummary.avg_accuracy_percent)} tone="text-emerald-600" testId="accuracy-average-percent" />
          <MetricCard label="Avg Price Variance" value={money(accuracySummary.avg_price_variance)} tone={Number(accuracySummary.avg_price_variance || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'} testId="accuracy-price-variance" />
          <MetricCard label="Avg Profit Variance" value={money(accuracySummary.avg_profit_variance)} tone={Number(accuracySummary.avg_profit_variance || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'} testId="accuracy-profit-variance" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="rounded-[1.5rem] border border-blue-100 bg-white shadow-sm sm:rounded-md">
            <CardHeader className="pb-2"><CardTitle className="text-xl font-black font-heading flex items-center gap-2 sm:text-base sm:font-bold"><Calculator className="w-5 h-5 text-blue-600 sm:w-4 sm:h-4" /> Accuracy Buckets</CardTitle></CardHeader>
            <CardContent data-testid="estimate-accuracy-bucket-chart">
              {accuracyBucketData.every(item => item.count === 0) ? <p className="text-sm text-zinc-500 py-8 text-center">Record job actuals to unlock accuracy reporting</p> : <ResponsiveContainer width="100%" height={240}><PieChart><Pie data={accuracyBucketData} cx="50%" cy="50%" innerRadius={48} outerRadius={88} dataKey="count" label={({ name, count }) => `${name}: ${count}`}>{accuracyBucketData.map(entry => <Cell key={entry.name} fill={entry.fill} />)}</Pie><Tooltip /></PieChart></ResponsiveContainer>}
            </CardContent>
          </Card>
          <Card className="rounded-[1.5rem] border border-violet-100 bg-white shadow-sm sm:rounded-md">
            <CardHeader className="pb-2"><CardTitle className="text-xl font-black font-heading sm:text-base sm:font-bold">Accuracy by Load Category</CardTitle></CardHeader>
            <CardContent data-testid="estimate-accuracy-category-chart">
              {accuracyByCategory.length === 0 ? <p className="text-sm text-zinc-500 py-8 text-center">No actuals recorded yet</p> : <ResponsiveContainer width="100%" height={240}><BarChart data={accuracyByCategory} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" /><XAxis type="number" tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} /><YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} /><Tooltip formatter={(v, name) => name === 'accuracy' ? `${Number(v).toFixed(1)}%` : v} /><Bar dataKey="accuracy" fill="#8b5cf6" radius={[0, 4, 4, 0]} /></BarChart></ResponsiveContainer>}
            </CardContent>
          </Card>
        </div>
        <Card className="rounded-[1.5rem] border border-orange-100 bg-white shadow-sm sm:rounded-md">
          <CardHeader className="pb-2"><CardTitle className="text-xl font-black font-heading sm:text-base sm:font-bold">Recent Estimate Variance</CardTitle></CardHeader>
          <CardContent data-testid="estimate-accuracy-recent-table">
            {recentAccuracy.length === 0 ? <p className="text-sm text-zinc-500 py-6 text-center">No completed actual-profit records yet</p> : <div className="overflow-x-auto"><table className="min-w-full text-sm"><thead><tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wide text-zinc-500"><th className="py-2 pr-4">Estimate</th><th className="py-2 pr-4">Category</th><th className="py-2 pr-4">Accuracy</th><th className="py-2 pr-4">Estimated</th><th className="py-2 pr-4">Actual</th><th className="py-2 pr-4">Profit Variance</th></tr></thead><tbody>{recentAccuracy.map(record => <tr key={record.id} className="border-b border-zinc-100" data-testid={`estimate-accuracy-row-${record.id}`}><td className="py-2 pr-4 font-semibold">{record.estimate_number}</td><td className="py-2 pr-4 text-zinc-600">{record.load_category_name}</td><td className="py-2 pr-4"><span className="rounded-full bg-zinc-100 px-2 py-1 text-xs font-semibold">{pct(record.accuracy_percent)}</span></td><td className="py-2 pr-4">{money(record.estimated_price)}</td><td className="py-2 pr-4">{money(record.actual_price)}</td><td className={`py-2 pr-4 font-semibold ${Number(record.profit_variance || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{money(record.profit_variance)}</td></tr>)}</tbody></table></div>}
          </CardContent>
        </Card>
      </section>

      <section className="space-y-4" data-testid="profitability-bi-section">
        <div className="flex flex-col gap-1">
          <h2 className="text-2xl font-black tracking-tight text-zinc-950 font-heading flex items-center gap-2" data-testid="profitability-bi-title"><Trophy className="h-6 w-6 text-emerald-600" /> Profitability Intelligence</h2>
          <p className="text-base font-semibold text-zinc-600">Actual job-cost performance by job type and lead source over the last 180 days.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="profitability-bi-kpis">
          <MetricCard label="Actual Profit" value={money(profitSummary.total_profit)} tone="text-emerald-600" testId="profitability-total-profit" />
          <MetricCard label="Avg Margin" value={pct(profitSummary.avg_margin)} tone="text-zinc-950" testId="profitability-average-margin" />
          <MetricCard label="Avg Job Value" value={money(profitSummary.average_job_value)} tone="text-sky-600" testId="profitability-average-job-value" />
          <MetricCard label="Repeat Rate" value={pct(profitSummary.repeat_customer_rate)} tone="text-amber-600" testId="profitability-repeat-rate" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="profitability-cost-kpis">
          <MetricCard label="Avg Dump Cost" value={money(profitSummary.average_dump_cost)} tone="text-zinc-950" testId="profitability-average-dump-cost" />
          <MetricCard label="Avg Fuel Cost" value={money(profitSummary.average_fuel_cost)} tone="text-zinc-950" testId="profitability-average-fuel-cost" />
          <MetricCard label="Avg Labor Hours" value={Number(profitSummary.average_labor_hours || 0).toFixed(1)} tone="text-zinc-950" testId="profitability-average-labor-hours" />
          <MetricCard label="Quote Conversion" value={pct(profitSummary.quote_conversion_rate)} tone="text-emerald-600" testId="profitability-quote-conversion-rate" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ProfitChart title="Profit by Job Type" data={profitByJobType} testId="profit-by-job-type-chart" />
          <ProfitChart title="Profit by Lead Source" data={profitByLeadSource} testId="profit-by-lead-source-chart" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TopList title="Top Job Categories" items={topJobCategories} testId="top-job-categories-list" />
          <TopList title="Top Lead Sources" items={topLeadSources} testId="top-lead-sources-list" />
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="rounded-[1.5rem] border border-blue-100 bg-white shadow-sm sm:rounded-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-black font-heading sm:text-base sm:font-bold">Lead Pipeline</CardTitle>
          </CardHeader>
          <CardContent data-testid="lead-pipeline-chart">
            {leadStageData.length === 0 ? (
              <p className="text-sm text-zinc-500 py-8 text-center">No lead data</p>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={leadStageData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {leadStageData.map((entry) => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-[1.5rem] border border-emerald-100 bg-white shadow-sm sm:rounded-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-black font-heading sm:text-base sm:font-bold">Job Status</CardTitle>
          </CardHeader>
          <CardContent data-testid="job-status-chart">
            {jobStatusData.length === 0 ? (
              <p className="text-sm text-zinc-500 py-8 text-center">No job data</p>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={jobStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    dataKey="count"
                    label={({ name, count }) => `${name}: ${count}`}
                  >
                    {jobStatusData.map((entry) => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-[1.5rem] border border-orange-100 bg-white shadow-sm sm:rounded-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-black font-heading sm:text-base sm:font-bold">Lead Sources</CardTitle>
          </CardHeader>
          <CardContent data-testid="lead-sources-chart">
            {leadSourceData.length === 0 ? (
              <p className="text-sm text-zinc-500 py-8 text-center">No source data</p>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={leadSourceData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-[1.5rem] border border-violet-100 bg-white shadow-sm sm:rounded-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-black font-heading sm:text-base sm:font-bold">Staff Workload</CardTitle>
          </CardHeader>
          <CardContent data-testid="staff-workload-chart">
            {staffData.length === 0 ? (
              <p className="text-sm text-zinc-500 py-8 text-center">No staff data</p>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={staffData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="assigned" fill="#2563eb" name="Assigned" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="completed" fill="#10b981" name="Completed" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, tone, testId }) {
  return <Card className="rounded-[1.5rem] border border-blue-100 bg-white shadow-sm sm:rounded-md"><CardContent className="p-4"><p className="text-[10px] font-black uppercase tracking-wider text-blue-700">{label}</p><p className={`text-2xl font-black font-heading ${tone}`} data-testid={testId}>{value}</p></CardContent></Card>;
}

function ProfitChart({ title, data, testId }) {
  return <Card className="rounded-[1.5rem] border border-emerald-100 bg-white shadow-sm sm:rounded-md"><CardHeader className="pb-2"><CardTitle className="text-xl font-black font-heading sm:text-base sm:font-bold">{title}</CardTitle></CardHeader><CardContent data-testid={testId}>{data.length === 0 ? <p className="text-sm text-zinc-500 py-8 text-center">Complete jobs with actual costs to unlock this chart</p> : <ResponsiveContainer width="100%" height={260}><BarChart data={data} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" /><XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} /><YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} /><Tooltip formatter={(v) => money(v)} /><Bar dataKey="profit" fill="#10b981" radius={[0, 4, 4, 0]} /></BarChart></ResponsiveContainer>}</CardContent></Card>;
}

function TopList({ title, items, testId }) {
  return <Card className="rounded-[1.5rem] border border-violet-100 bg-white shadow-sm sm:rounded-md"><CardHeader className="pb-2"><CardTitle className="text-xl font-black font-heading sm:text-base sm:font-bold">{title}</CardTitle></CardHeader><CardContent data-testid={testId}>{items.length === 0 ? <p className="text-sm text-zinc-500 py-6 text-center">No actual-cost records yet</p> : <div className="space-y-2">{items.map(item => <div key={item.key} className="flex items-center justify-between rounded-2xl border border-violet-100 bg-violet-50 px-3 py-3 sm:rounded-md sm:bg-zinc-50" data-testid={`${testId}-${item.key}`}><div><p className="text-base font-black text-zinc-950 sm:text-sm">{item.label}</p><p className="text-xs font-semibold text-zinc-500">{item.count} jobs · {pct(item.avg_margin)} margin</p></div><div className="text-right"><p className="text-base font-black text-emerald-600 sm:text-sm">{money(item.profit)}</p><p className="text-xs text-zinc-500">{money(item.revenue)} revenue</p></div></div>)}</div>}</CardContent></Card>;
}
